<template>
    <main>
        <SideBar 
            :current-language="prismicLang"
            :clc-name="clcName"
            @toggle-language="toggleLanguage"
        />
        <section class="News-detail-anchor" data-component-name="News-detail">

            <!-- Loading State -->
            <div class="loading-state" v-if="isLoading">
                <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" style="text-align: center; padding: 60px 20px;">
                <p style="color: red;">{{ prismicLang === 'en-us' ? 'Error loading news:' : 'Error al cargar la noticia:' }} {{ error }}</p>
                <a :href="homeUrl" style="color: #009FDB; text-decoration: underline;">
                    {{ prismicLang === 'en-us' ? 'Back to Home' : 'Volver al Inicio' }}
                </a>
            </div>

            <!-- Content (only when data is loaded) -->
            <template v-else-if="newsData">
                <article class="top">
                    <figure>
                        <div class="box-data">
                            <div class="wrapper">
                                <h1>{{ newsTitle }}</h1>
                            </div>
                        </div>
                        <div class="box-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
                    </figure>
                </article>
                <article class="bottom">
                    <div class="head-news">
                        <p>{{ author }}</p>
                        <p v-if="publishedDate"><i>{{ prismicLang === 'en-us' ? 'Published' : 'Publicado' }}: {{ publishedDate }}</i></p>
                    </div>
                    <component
                        v-for="(slice, index) in bodySlices"
                        :key="`${slice.slice_type}-${index}`"
                        :is="getSliceComponent(slice)"
                        :slice-data="slice"
                        :prismic-lang="prismicLang"
                    />
                    <div class="footer-news">
                        <a :href="homeUrl" class="back-to-news">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none">
                                <path d="M21 10.5H1" stroke-width="2"/>
                                <path d="M10 1.5L1 10.5L10 19.5" stroke-width="2"/>
                            </svg>
                            <span>{{ prismicLang === 'en-us' ? 'Back to' : 'Volver a' }} <strong>{{ prismicLang === 'en-us' ? 'Home' : 'Inicio' }}</strong></span>
                        </a>
                    </div>
                </article>
            </template>
        </section>
        <footer>
            <a class="go-top" href="#" @click.prevent="scrollToTop" alt="Go Top">
                <svg v-if="prismicLang === 'en-us'" width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#49EEDC"/>
                <path d="M51.125 36.875L39.875 25.625L28.625 36.875" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M29.7659 51.045H26.6719V49.575H34.4699V51.045H31.3759V59.375H29.7659V51.045ZM39.7217 50.933C39.1244 50.933 38.597 51.087 38.1397 51.395C37.6824 51.703 37.3324 52.1277 37.0897 52.669C36.847 53.201 36.7257 53.803 36.7257 54.475C36.7257 55.175 36.8564 55.7957 37.1177 56.337C37.379 56.8783 37.7337 57.2937 38.1817 57.583C38.639 57.8723 39.1524 58.017 39.7217 58.017C40.319 58.017 40.8464 57.863 41.3037 57.555C41.761 57.247 42.111 56.827 42.3537 56.295C42.5964 55.7537 42.7177 55.147 42.7177 54.475C42.7177 53.775 42.587 53.1543 42.3257 52.613C42.0644 52.0717 41.705 51.6563 41.2477 51.367C40.7997 51.0777 40.291 50.933 39.7217 50.933ZM39.7217 49.421C40.6364 49.421 41.4437 49.645 42.1437 50.093C42.853 50.541 43.399 51.1477 43.7817 51.913C44.1644 52.6783 44.3557 53.5323 44.3557 54.475C44.3557 55.4643 44.1504 56.3463 43.7397 57.121C43.329 57.8957 42.7737 58.493 42.0737 58.913C41.3737 59.3237 40.5897 59.529 39.7217 59.529C38.807 59.529 37.995 59.305 37.2857 58.857C36.5857 58.409 36.0444 57.8023 35.6617 57.037C35.279 56.2717 35.0877 55.4177 35.0877 54.475C35.0877 53.4857 35.293 52.6037 35.7037 51.829C36.1144 51.0543 36.6697 50.4617 37.3697 50.051C38.0697 49.631 38.8537 49.421 39.7217 49.421ZM46.1158 49.575H49.4758C50.6051 49.575 51.4824 49.855 52.1078 50.415C52.7424 50.9657 53.0598 51.7403 53.0598 52.739C53.0598 53.747 52.7378 54.5403 52.0938 55.119C51.4591 55.6977 50.5864 55.987 49.4758 55.987H47.7258V59.375H46.1158V49.575ZM47.7258 54.573H49.3778C50.0218 54.573 50.5211 54.4143 50.8758 54.097C51.2398 53.7703 51.4218 53.3177 51.4218 52.739C51.4218 52.179 51.2444 51.7497 50.8898 51.451C50.5351 51.143 50.0311 50.989 49.3778 50.989H47.7258V54.573Z" fill="white"/>
                </svg>

                <svg v-else width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#49EEDC"/>
                <path d="M51.125 33.875L39.875 22.625L28.625 33.875" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M24.2481 56.529C22.9415 56.529 21.8308 56.249 20.9161 55.689L21.3781 54.359C22.2648 54.8163 23.1935 55.045 24.1641 55.045C24.8268 55.045 25.3355 54.9237 25.6901 54.681C26.0541 54.4383 26.2361 54.093 26.2361 53.645C26.2361 53.4023 26.1801 53.2017 26.0681 53.043C25.9655 52.8843 25.7788 52.7397 25.5081 52.609C25.2468 52.4783 24.8315 52.329 24.2621 52.161L23.6741 51.993C22.8061 51.7503 22.1761 51.4143 21.7841 50.985C21.3921 50.5463 21.1961 49.9723 21.1961 49.263C21.1961 48.3857 21.4995 47.695 22.1061 47.191C22.7128 46.6777 23.5435 46.421 24.5981 46.421C25.1395 46.421 25.6621 46.4863 26.1661 46.617C26.6701 46.7383 27.1275 46.9203 27.5381 47.163L27.1041 48.465C26.7495 48.2877 26.3621 48.1523 25.9421 48.059C25.5221 47.9563 25.1068 47.905 24.6961 47.905C24.0988 47.905 23.6368 48.017 23.3101 48.241C22.9928 48.4557 22.8341 48.773 22.8341 49.193C22.8341 49.529 22.9555 49.7997 23.1981 50.005C23.4501 50.2103 23.8888 50.4017 24.5141 50.579L25.1021 50.747C25.7741 50.943 26.3108 51.1623 26.7121 51.405C27.1135 51.6383 27.4075 51.9277 27.5941 52.273C27.7808 52.609 27.8741 53.0337 27.8741 53.547C27.8741 54.471 27.5475 55.199 26.8941 55.731C26.2501 56.263 25.3681 56.529 24.2481 56.529ZM33.3853 56.529C32.1533 56.529 31.2153 56.1837 30.5713 55.493C29.9367 54.8023 29.6193 53.7943 29.6193 52.469V46.575H31.2293V52.427C31.2293 53.3043 31.4067 53.9577 31.7613 54.387C32.116 54.807 32.6573 55.017 33.3853 55.017C34.1133 55.017 34.6547 54.807 35.0093 54.387C35.364 53.9577 35.5413 53.3043 35.5413 52.427V46.575H37.1513V52.469C37.1513 53.7943 36.8293 54.8023 36.1853 55.493C35.5507 56.1837 34.6173 56.529 33.3853 56.529ZM39.2183 46.575H42.9283C43.9923 46.575 44.8043 46.7943 45.3643 47.233C45.9243 47.6717 46.2043 48.3063 46.2043 49.137C46.2043 49.6597 46.0596 50.1217 45.7703 50.523C45.4903 50.915 45.103 51.195 44.6083 51.363C45.215 51.4843 45.6816 51.7457 46.0083 52.147C46.335 52.5483 46.4983 53.057 46.4983 53.673C46.4983 54.2237 46.3583 54.7043 46.0783 55.115C45.8076 55.5257 45.425 55.8383 44.9303 56.053C44.4356 56.2677 43.8616 56.375 43.2083 56.375H39.2183V46.575ZM40.8283 52.119V55.031H43.2083C43.7216 55.031 44.123 54.905 44.4123 54.653C44.711 54.3917 44.8603 54.0323 44.8603 53.575C44.8603 53.099 44.7063 52.7397 44.3983 52.497C44.0903 52.245 43.6376 52.119 43.0403 52.119H40.8283ZM40.8283 47.919V50.775H42.9283C43.451 50.775 43.857 50.649 44.1463 50.397C44.445 50.145 44.5943 49.7903 44.5943 49.333C44.5943 48.8757 44.4496 48.5257 44.1603 48.283C43.871 48.0403 43.4603 47.919 42.9283 47.919H40.8283ZM48.1597 46.575H49.7697V56.375H48.1597V46.575ZM55.2941 52.791H53.5021V56.375H51.8921V46.575H55.3641C56.4655 46.575 57.3195 46.8457 57.9261 47.387C58.5328 47.919 58.8361 48.6703 58.8361 49.641C58.8361 50.3317 58.6635 50.929 58.3181 51.433C57.9821 51.937 57.5061 52.3057 56.8901 52.539L59.2701 56.375H57.4221L55.2941 52.791ZM53.5021 51.377H55.3501C55.9195 51.377 56.3675 51.223 56.6941 50.915C57.0301 50.607 57.1981 50.1917 57.1981 49.669C57.1981 49.1463 57.0348 48.7357 56.7081 48.437C56.3815 48.1383 55.9288 47.989 55.3501 47.989H53.5021V51.377Z" fill="white"/>
                </svg>
            </a>
            <p>
                <img src="../assets/images/rebrand-2026/logo-footer.png" alt="ATT Logo" />
                <span>Corporate Responsibility</span>
            </p>
            <ul>
                <li>
                    <a href="https://www.att.com/" target="_blank" rel="noopener" alt="ATT">www.att.com</a>
                </li>
                <li>
                    <a href="https://sustainability.att.com/" target="_blank" rel="noopener" alt="Sustainability ATT">sustainability.att.com</a>
                </li>
            </ul>
        </footer>
    </main>
</template>

<script>
import SideBar from './SideBar.vue';
import RichText from './subcomponents/RichText.vue';
import CtaButton from './subcomponents/CtaButton.vue';
import ImageWrapper from './subcomponents/ImageWrapper.vue';
import LeyendComponent from './subcomponents/Leyend.vue';
import VideoWrapper from './subcomponents/VideoWrapper.vue';
import QuoteComponent from './subcomponents/Quote.vue';
import BannerImage from './subcomponents/BannerImage.vue';
import GalleryImage from './subcomponents/GalleryImage.vue';
import Const from '../constants';
import { getCleanPrismicImageUrl } from './helpers/prismicHelpers';

export default {
    name: 'CLCNewsDetail',
    components: {
        SideBar,
        RichText,
        CtaButton,
        ImageWrapper,
        LeyendComponent,
        VideoWrapper,
        QuoteComponent,
        BannerImage,
        GalleryImage
    },
    data() {
        return {
            prismicLang: 'en-us',
            clcName: sessionStorage.getItem('clcName') || '',
            clcSegmentValue: sessionStorage.getItem('clcSegmentValue') || '',
            newsData: null,
            isLoading: true,
            error: null
        }
    },
    computed: {
        newsTitle() {
            // Usar news_title en lugar de title
            return this.newsData?.data?.news_title?.[0]?.text || 'Untitled';
        },
        heroImage() {
            // Usar news_image en lugar de hero_image
            const url = this.newsData?.data?.news_image?.url || '';
            return getCleanPrismicImageUrl(url);
        },
        author() {
            // Author se mantiene igual
            return this.newsData?.data?.author || '';
        },
        publishedDate() {
            // Published date se mantiene igual
            const dateStr = this.newsData?.first_publication_date || '';
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const monthsEN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthsES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            const months = this.prismicLang === 'en-us' ? monthsEN : monthsES;
            return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
        },
        newsBody() {
            // Agregar computed para news_body (si lo necesitas mostrar en algún lugar)
            return this.newsData?.data?.news_body?.[0]?.text || '';
        },
        bodySlices() {
            // Los slices vienen del campo 'body'
            return this.newsData?.data?.body || [];
        },
        homeUrl() {
            const lang = this.prismicLang === 'en-us' ? 'en' : 'es';
            if (this.clcSegmentValue) {
                return `/${lang}/?center=${this.clcSegmentValue}`;
            }
            return `/${lang}/`;
        }
    },
    methods: {
        toggleLanguage() {
            // Obtener la última homepage visitada desde sessionStorage
            const lastHomepage = sessionStorage.getItem('lastHomepage');
            const newLang = this.prismicLang === 'en-us' ? 'es' : 'en';
            
            let redirectPath;
            if (!lastHomepage) {
                redirectPath = `/${newLang}/`;
            } else {
                // Separar path y query params
                const [path, query] = lastHomepage.split('?');
                const queryString = query ? `?${query}` : '';
                
                let newPath;
                
                // Si la ruta es la raíz
                if (path === '/') {
                    newPath = `/${newLang}/`;
                } 
                // Si ya tiene idioma, reemplazarlo
                else if (path.match(/^\/(en|es)\//)) {
                    newPath = path.replace(/^\/(en|es)\//, `/${newLang}/`);
                } 
                // Fallback
                else {
                    newPath = `/${newLang}/`;
                }
                
                redirectPath = newPath + queryString;
            }
            
            // Redirigir a la homepage correspondiente
            this.$router.push(redirectPath);
        },

        setLanguage() {
            if (this.$route.path.startsWith('/es')) {
                this.prismicLang = 'es-es';
            } else if (this.$route.path.startsWith('/en')) {
                this.prismicLang = 'en-us';
            }
        },

        getSliceComponent(slice) {
            const sliceType = slice.slice_type;
            
            // Standard slice type mapping
            const sliceMap = {
                'text_block': 'RichText',
                'cta': 'CtaButton',
                'image': 'ImageWrapper',
                'legend_text': 'LeyendComponent',
                'video': 'VideoWrapper',
                'quote': 'QuoteComponent',
                'highlight': 'BannerImage',
                'images_carousel': 'GalleryImage'
            };
            
            return sliceMap[sliceType] || null;
        },

        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        async getNewsDetail() {
            try {
                this.isLoading = true;
                this.error = null;

                // Get the news ID from route params
                const newsId = this.$route.params.id;

                if (!newsId) {
                    throw new Error('No news ID provided');
                }

                // Query Prismic for the specific news document by UID
                const response = await this.$prismic.client.getByUID(
                    Const.CLC_NEWS,
                    newsId,
                    {
                        lang: this.prismicLang
                    }
                );

                this.newsData = response;
                console.log('📰 News detail loaded:', this.newsData);

            } catch (error) {
                console.error('Error loading news detail:', error);
                this.error = error.message;
            } finally {
                this.isLoading = false;
                
                // Scroll to content on mobile after data is fully loaded
                this.$nextTick(() => {
                    if (window.innerWidth <= 640) {
                        const target = document.querySelector('.News-detail-anchor');
                        if (target) {
                            setTimeout(() => {
                                const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
                                window.scrollTo({ 
                                    top: targetPosition,
                                    behavior: 'smooth'
                                });
                            }, 500); // Increased delay to ensure all content including images are rendered
                        }
                    }
                });
            }
        }
    },
    async created() {
        this.setLanguage();
        await this.getNewsDetail();
    },
    watch: {
        // Reload data when language changes
        prismicLang() {
            this.getNewsDetail();
        }
    }
}
</script>